import { useState, FC } from 'react';
import {
  Modal,
  Stack,
  MultiSelect,
  Group,
  Button,
  ActionIcon,
  Text,
  Box,
  Paper,
  Badge,
  Divider,
} from '@mantine/core';
import { Practitioner } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { useTranslation } from 'react-i18next';
import { useMedplumProfile, ResourceAvatar } from '@medplum/react';
import { useReviewStatus } from '../hooks/useReviewStatus';
import { useParams } from 'react-router-dom';
import { usePractitioners } from '@/hooks/usePractitioners';
import { IconTrash, IconClock, IconChecklist } from '@tabler/icons-react';
import DeleteConfirmModal from '@/pages/ainigma/patient/images-tab/DeleteModal';
import styles from './ReviewerSelectModal.module.css';

// -----------------------------------------------------------------------------
interface ReviewerSelectModalProps {
  opened: boolean;
  onClose: () => void;
  onAddReviewers: (reviewerIds: string[]) => Promise<void> | void;
  onRemoveReviewer?: (reviewerId: string) => Promise<void> | void;
}

// -----------------------------------------------------------------------------
export const ReviewerSelectModal: FC<ReviewerSelectModalProps> = ({
  opened,
  onClose,
  onAddReviewers,
  onRemoveReviewer,
}) => {
  const { t } = useTranslation(['lims', 'common']);
  const currentUser = useMedplumProfile() as Practitioner;

  const { reportId } = useParams<{ reportId?: string }>();

  const { practitioners, getPractitioner, getPractitionerName } =
    usePractitioners();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Delete confirmation state
  const [removeTargetId, setRemoveTargetId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const { reviewStatusData } = useReviewStatus(reportId, currentUser);

  // IDs to exclude from the MultiSelect (already assigned reviewers)
  const excludedIds = reviewStatusData?.allReviewersIds ?? [];

  const handleConfirm = async () => {
    if (!selectedIds.length) {
      return;
    }
    setLoading(true);
    try {
      await onAddReviewers(selectedIds);
      setSelectedIds([]);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }
    setSelectedIds([]);
    onClose();
  };

  const handleRemoveConfirm = async () => {
    if (!removeTargetId || !onRemoveReviewer) {
      return;
    }
    setRemoving(true);
    try {
      await onRemoveReviewer(removeTargetId);
    } finally {
      setRemoving(false);
      setRemoveTargetId(null);
    }
  };

  // ── Reviewer row ──────────────────────────────────────────────────────────
  const ReviewerRow: FC<{ id: string; status: 'pending' | 'approved' }> = ({
    id,
    status,
  }) => {
    const practitioner = getPractitioner(id);
    return (
      <Paper
        className={styles.reviewerRow}
        withBorder
        bg={status === 'approved' ? 'green.0' : undefined}
        p="xs"
        radius="md"
        shadow="xs"
      >
        <Group justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <ResourceAvatar value={practitioner} radius="xl" size="sm" />
            <Text size="sm">{getPractitionerName(id)}</Text>
          </Group>
          <Group gap="xs">
            <Badge
              variant="light"
              color={status === 'approved' ? 'green' : 'orange'}
            >
              {status === 'approved'
                ? t('review.comments.statusLabels.approved')
                : t('review.comments.statusLabels.pending')}
            </Badge>

            {status === 'pending' && onRemoveReviewer && (
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                onClick={() => setRemoveTargetId(id)}
                aria-label="Remove reviewer"
              >
                <IconTrash size={14} />
              </ActionIcon>
            )}
          </Group>
        </Group>
      </Paper>
    );
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleClose}
        title={
          <Stack gap={0}>
            <Text fw={600}>
              {t('review.status.editReviewers', { ns: 'lims' })}
            </Text>
            <Text c="dimmed" size="xs">
              {t('review.status.reviewersSummary', {
                ns: 'lims',
                assigned: reviewStatusData?.allReviewersIds.length ?? 0,
                approved: reviewStatusData?.approvedReviewerIds.length ?? 0,
              })}
            </Text>
          </Stack>
        }
        size="md"
      >
        <Stack gap="md">
          {/* Pending reviewers */}
          {(reviewStatusData?.pendingReviewerIds?.length ?? 0) > 0 && (
            <Box>
              <Group mb="xs">
                <Group gap="xs">
                  <IconClock color="gray" size={16} />
                  <Text size="sm" fw={500}>
                    {t('review.status.pendingReviewers', { ns: 'lims' })}
                  </Text>
                </Group>
                <Badge variant="light" color="orange">
                  {reviewStatusData?.pendingReviewerIds?.length}
                </Badge>
              </Group>
              <Stack gap="xs">
                {reviewStatusData?.pendingReviewerIds.map((id) => (
                  <ReviewerRow key={id} id={id} status="pending" />
                ))}
              </Stack>
            </Box>
          )}

          {/* Approved reviewers */}
          {(reviewStatusData?.approvedReviewerIds?.length ?? 0) > 0 && (
            <Box>
              <Group mb="xs">
                <Group gap="xs">
                  <IconChecklist size={16} color="gray" />
                  <Text size="sm" fw={500}>
                    {t('review.status.approvedReviewers', { ns: 'lims' })}
                  </Text>
                </Group>
                <Badge variant="light">
                  {reviewStatusData?.approvedReviewerIds?.length}
                </Badge>
              </Group>

              <Stack gap="xs">
                {reviewStatusData?.approvedReviewerIds.map((id) => (
                  <ReviewerRow key={id} id={id} status="approved" />
                ))}
              </Stack>
            </Box>
          )}

          <Divider
            label={t('reportEditor.addReviewers', { ns: 'lims' })}
            labelPosition="center"
          />

          <MultiSelect
            placeholder={t('orderDetails.chooseReviewers', { ns: 'lims' })}
            value={selectedIds}
            onChange={setSelectedIds}
            data={practitioners
              .filter((p) => !excludedIds.includes(p.id ?? ''))
              .map((p) => ({
                value: p.id ?? '',
                label: formatHumanName(p.name?.[0]) || p.id || '',
              }))}
            searchable
            clearable
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose} disabled={loading}>
              {t('buttons.cancel', { ns: 'common' })}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!selectedIds.length}
              loading={loading}
            >
              {t('buttons.add', { ns: 'common' })}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <DeleteConfirmModal
        opened={!!removeTargetId}
        onClose={() => setRemoveTargetId(null)}
        onConfirm={handleRemoveConfirm}
        loading={removing}
        title={t('review.status.confirmRemoveTitle', { ns: 'lims' })}
        message={t('review.status.confirmRemoveMessage', {
          ns: 'lims',
          name: removeTargetId ? getPractitionerName(removeTargetId) : '',
        })}
      />
    </>
  );
};
